import lzstring from 'lz-string'
import {
    cleanFiles,
    ProjectFormatError,
    type BuildSources,
    type ProjectFiles
} from '#lib/projectFiles.ts'
import type { Project } from '#lib/Project.svelte.ts'
import {
    cleanCompilationRecords,
    sourceLanguage,
    type CompilationRecord
} from '#lib/sourceCompilation/records.ts'
import { programBuildSources } from '#lib/buildSources.ts'

/** Named files passed to an embed; the target remains the embed's `language` setting. */
export type PlaygroundProgram = {
    files: ProjectFiles
    entry: string
    compilations?: CompilationRecord[]
}

export function cleanPlaygroundProgram(raw: unknown): PlaygroundProgram {
    if (!raw || typeof raw !== 'object') throw new ProjectFormatError('Invalid playground program')
    const value = raw as Record<string, unknown>
    const files = cleanFiles(value.files)
    if (
        typeof value.entry !== 'string' ||
        !Object.prototype.hasOwnProperty.call(files, value.entry)
    )
        throw new ProjectFormatError('The playground entry must name one of its files')
    if (Object.values(files).some((file) => file.encoding !== 'plain'))
        throw new ProjectFormatError('Playground files must be text')
    const compilations = cleanCompilationRecords(value.compilations)
    return { files, entry: value.entry, ...(compilations ? { compilations } : {}) }
}

export function encodePlaygroundProgram(program: PlaygroundProgram): string {
    return lzstring.compressToEncodedURIComponent(JSON.stringify(cleanPlaygroundProgram(program)))
}

export function decodePlaygroundProgram(encoded: string): PlaygroundProgram {
    const text = lzstring.decompressFromEncodedURIComponent(encoded)
    if (!text) throw new ProjectFormatError('Unreadable playground program')
    return cleanPlaygroundProgram(JSON.parse(text))
}

/**
 * Use the same assembler provenance and runtime startup as a Project Build, for the Playground's
 * one Program (programs.md, decision 12).
 */
export function playgroundBuildSources(project: Project): BuildSources {
    const entry = project.selectedProgram
    if (entry !== undefined && sourceLanguage(entry)) {
        //There is no assembly to check yet. The Build action compiles the real source before
        //using this input; keep the emulator idle without marking valid C as an assembly error.
        return { files: {}, entry }
    }
    return programBuildSources(project)
}
